import { useState, useEffect } from 'react';
import { adminAPI, ordersAPI, depositsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Users, Package, DollarSign, AlertCircle, RotateCcw } from 'react-feather';
import { formatCurrencyWithConversion } from '../utils/currency';
import './AdminDashboard.css';

const STATUS_COLORS = {
  pending: 'badge-pending', in_progress: 'badge-in_progress',
  completed: 'badge-completed', partial: 'badge-partial', canceled: 'badge-canceled',
  refunded: 'badge-canceled'
};

function getDisplayStatus(order) {
  return order?.refund_status === 'refunded' || order?.refund_status === 'manual_refunded'
    ? 'refunded'
    : order?.status;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentDeposits, setRecentDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadMsg, setLoadMsg] = useState('');
  const [verifyingDepositId, setVerifyingDepositId] = useState(null);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      setLoadMsg('');

      try {
        const [statsRes, usersRes, recentOrdersRes, depositsRes] = await Promise.allSettled([
          adminAPI.stats(),
          adminAPI.users(),
          ordersAPI.adminRecent(),
          depositsAPI.adminAll(),
        ]);

        const statsPayload = statsRes.status === 'fulfilled' ? (statsRes.value.data?.stats || {}) : {};
        const users = usersRes.status === 'fulfilled' ? (usersRes.value.data?.users || []) : [];
        const recentPayload = recentOrdersRes.status === 'fulfilled' ? (recentOrdersRes.value.data || {}) : {};
        const deposits = depositsRes.status === 'fulfilled' ? (depositsRes.value.data?.deposits || []) : [];

        const fallbackRevenue = deposits
          .filter((deposit) => deposit.status === 'completed')
          .reduce((sum, deposit) => sum + (parseFloat(deposit.amount) || 0), 0);

        const fallbackPending = (recentPayload.orders || [])
          .filter((order) => ['pending', 'in_progress'].includes(getDisplayStatus(order)))
          .length;

        const fallbackRefunds = (recentPayload.orders || [])
          .reduce((sum, order) => sum + (parseFloat(order.refund_amount) || 0), 0);

        const chooseCount = (primaryValue, fallbackValue) => {
          const primary = Number(primaryValue);
          const fallback = Number(fallbackValue);
          if (Number.isFinite(fallback) && fallback > 0 && (!Number.isFinite(primary) || primary === 0)) {
            return fallback;
          }
          return Number.isFinite(primary) ? primary : fallback;
        };

        setStats({
          total_users: chooseCount(statsPayload.total_users, users.length),
          total_orders: chooseCount(statsPayload.total_orders, recentPayload.total || 0),
          total_revenue: chooseCount(statsPayload.total_revenue, fallbackRevenue),
          pending_orders: chooseCount(statsPayload.pending_orders, fallbackPending),
          total_refunds: chooseCount(statsPayload.total_refunds, fallbackRefunds),
        });
        const statsRecentOrders = statsRes.status === 'fulfilled' ? (statsRes.value.data?.recent_orders || []) : [];
        setRecentOrders(statsRecentOrders.length > 0 ? statsRecentOrders : (recentPayload.orders || []));
        setRecentDeposits(deposits.slice(0, 10));

        if (statsRes.status !== 'fulfilled') {
          setLoadMsg('Dashboard stats endpoint failed, so fallback data is being shown.');
        }
      } catch (error) {
        setLoadMsg(error.response?.data?.error || 'Failed to load dashboard data');
        setStats({
          total_users: 0,
          total_orders: 0,
          total_revenue: 0,
          pending_orders: 0,
          total_refunds: 0,
        });
        setRecentOrders([]);
        setRecentDeposits([]);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  if (loading) return <div className="loading-screen" style={{ height: 300 }}><div className="spinner" /></div>;

  const handleMarkDepositVerified = async (depositId) => {
    setVerifyingDepositId(depositId);
    setLoadMsg('');
    try {
      const response = await depositsAPI.adminMarkVerified(depositId);
      const updatedDeposit = response.data?.deposit;
      setRecentDeposits((prev) => prev.map((deposit) => (
        deposit.id === depositId ? { ...deposit, ...(updatedDeposit || {}), status: 'completed' } : deposit
      )));
      setLoadMsg(response.data?.message || 'Deposit marked as verified.');
    } catch (error) {
      setLoadMsg(error.response?.data?.error || 'Failed to mark deposit as verified');
    } finally {
      setVerifyingDepositId(null);
    }
  };

  const statCards = [
    { label: 'Total Users', value: stats?.total_users?.toLocaleString(), icon: Users, accent: 'stat-accent-blue' },
    { label: 'Total Orders', value: stats?.total_orders?.toLocaleString(), icon: Package, accent: 'stat-accent-orange' },
    { label: 'Total Revenue', value: formatCurrencyWithConversion(stats?.total_revenue || 0, user?.currency || 'USD'), icon: DollarSign, accent: 'stat-accent-green' },
    { label: 'Pending Orders', value: stats?.pending_orders?.toLocaleString(), icon: AlertCircle, accent: 'stat-accent-purple' },
    { label: 'Total Refunds', value: formatCurrencyWithConversion(stats?.total_refunds || 0, user?.currency || 'USD'), icon: RotateCcw, accent: 'stat-accent-blue' },
  ];

  return (
    <div>
      <h1 className="page-title">Admin Dashboard</h1>
      <p className="page-subtitle">Platform overview and recent activity</p>
      {loadMsg && <div className="alert alert-error" style={{ marginBottom: 16 }}>{loadMsg}</div>}

      <div className="admin-stats-grid" style={{ marginBottom: 28 }}>
        {statCards.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className={`stat-card ${s.accent}`}>
              <div className="stat-icon"><Icon size={32} /></div>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: 18, fontSize: 18 }}>Recent Orders</h3>
        <div className="table-wrap admin-dashboard-recent-table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th><th>User</th><th>Service</th><th>Cost</th><th>Status</th><th>Date</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map(o => (
                (() => {
                  const displayStatus = getDisplayStatus(o);
                  return (
                  <tr key={o.id}>
                    <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{o.id}</td>
                    <td>
                      <div className="admin-dashboard-recent-username">{o.username}</div>
                    </td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.service_name}</td>
                    <td style={{ color: 'var(--orange)', fontWeight: 600 }}>{formatCurrencyWithConversion(o.cost, user?.currency || 'USD', 4)}</td>
                    <td><span className={`badge ${STATUS_COLORS[displayStatus] || 'badge-pending'}`}>{displayStatus.replace('_', ' ')}</span></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{new Date(o.created_at).toLocaleString()}</td>
                  </tr>
                  );
                })()
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <h3 style={{ marginBottom: 18, fontSize: 18 }}>Recent Deposits</h3>
        <div className="table-wrap admin-dashboard-recent-table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th><th>User</th><th>Gateway</th><th>Amount</th><th>Status</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {recentDeposits.map((deposit) => (
                <tr key={deposit.id}>
                  <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{deposit.id}</td>
                  <td><div className="admin-dashboard-recent-username">{deposit.username}</div></td>
                  <td style={{ textTransform: 'capitalize' }}>{deposit.gateway}</td>
                  <td style={{ color: 'var(--orange)', fontWeight: 600 }}>
                    {formatCurrencyWithConversion(deposit.amount, user?.currency || 'USD', 4)}
                  </td>
                  <td>
                    <span className={`badge ${deposit.status === 'completed' ? 'badge-completed' : deposit.status === 'failed' ? 'badge-canceled' : 'badge-pending'}`}>
                      {deposit.status}
                    </span>
                  </td>
                  <td>
                    {deposit.status === 'pending' ? (
                      <button
                        className="btn btn-primary btn-sm admin-dashboard-action-btn"
                        type="button"
                        disabled={verifyingDepositId === deposit.id}
                        onClick={() => handleMarkDepositVerified(deposit.id)}
                      >
                        {verifyingDepositId === deposit.id ? 'Verifying...' : 'Mark Verified'}
                      </button>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>No action</span>
                    )}
                  </td>
                </tr>
              ))}
              {recentDeposits.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>
                    No deposits yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
